import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { Logo } from '@/components/Logo'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await apiFetch('/auth/forgot-password', {
        method: 'POST',
        body: { email },
        skipAuth: true,
      })
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-gradient-to-b from-ovation-paper to-white px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        <div className="rounded-xl border border-ovation-ink/10 bg-white p-7 shadow-xl shadow-ovation-ink/5">
          {submitted ? (
            <div className="space-y-3 text-sm">
              <h1 className="font-display text-2xl tracking-tight">Check your email</h1>
              <p className="text-ovation-muted">
                If an account exists for <strong>{email}</strong>, a reset link is on
                its way. The link expires in one hour.
              </p>
              <p className="text-xs text-ovation-muted">
                In development the reset token is logged to the API server console
                until Brevo email delivery is wired up.
              </p>
              <Link
                to="/login"
                className="mt-4 inline-block text-sm font-medium text-ovation-ink hover:underline"
              >
                ← Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <h1 className="font-display text-2xl tracking-tight">Reset password</h1>
                <p className="mt-1 text-sm text-ovation-muted">
                  Enter the email associated with your account.
                </p>
              </div>
              <div>
                <label htmlFor="email" className="mb-1 block text-sm font-medium">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
                />
              </div>

              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper transition-colors hover:bg-ovation-ink/90 disabled:opacity-50"
              >
                {submitting ? 'Sending…' : 'Send reset link'}
              </button>

              <div className="text-center text-xs text-ovation-muted">
                <Link to="/login" className="hover:text-ovation-ink">
                  ← Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
